



import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Steven Eric Javidi",
    "jobTitle": "Houston Storyteller & Guide Writer",
    "description": "Houston-based storyteller exploring where the city's history meets its natural landscapes",
    "url": import.meta.env.VITE_SITE_URL,
    "sameAs": [],
    "knowsAbout": [
      "Houston History",
      "Houston Neighborhoods",
      "Buffalo Bayou",
      "Houston Heights",
      "Urban Exploration",
      "Cultural Heritage"
    ],
    "areaServed": {
      "@type": "City",
      "name": "Houston",
      "addressRegion": "Texas",
      "addressCountry": "US"
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      <SEO
        title="Steven Eric Javidi - Houston Stories & Exploration Guides"
        description="Explore Houston's stories with Steven Eric Javidi. Research-driven guides to Houston's historic neighborhoods, natural spaces, and cultural landmarks."
        keywords="Houston guides, Houston history, Houston neighborhoods, Buffalo Bayou, Heights Houston, Houston tours, Houston exploration, Steven Eric Javidi"
        ogTitle="Steven Eric Javidi - Houston Stories & Exploration Guides"
        ogDescription="Explore Houston's stories with Steven Eric Javidi. Research-driven guides to Houston's historic neighborhoods, natural spaces, and cultural landmarks."
        ogType="website"
        ogUrl={import.meta.env.VITE_SITE_URL}
        twitterTitle="Steven Eric Javidi - Houston Stories & Exploration Guides"
        twitterDescription="Explore Houston's stories with Steven Eric Javidi. Research-driven guides to Houston's historic neighborhoods, natural spaces, and cultural landmarks."
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="bg-neutral-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-semibold text-white">
                Eric Javidi
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <Link to="/" className="text-white hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Home
                </Link>
                <Link to="/about" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  About
                </Link>
                <Link to="/guides" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Guides
                </Link>
                <Link to="/holiday-season" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Holiday Season
                </Link>
                <Link to="/fall-festivals" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Fall Festivals
                </Link>
                <Link to="/resources" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Resources
                </Link>
                <Link to="/contact" className="text-neutral-300 hover:text-green-400 px-3 py-2 text-sm font-medium">
                  Contact
                </Link>
              </div>
            </div>
            <div className="md:hidden">
              <button className="text-neutral-300 hover:text-white">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/eaa07a3c94a4907ce6167ad27255b789.jpg')`
          }}
        ></div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-neutral-800/50"></div>
        
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
              Steven Eric Javidi: Exploring Houston's Stories
            </h1>
            <p className="text-xl lg:text-2xl text-neutral-100 mb-8">
              From historic neighborhoods to natural escapes, Eric Javidi shares research-driven insights on the city.
            </p>
          </div>
        </div>
      </section>

      {/* Steven's Photo Section - MOVED BELOW HERO BANNER */}
      <section className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden shadow-lg">
                <img
                  src="https://static.readdy.ai/image/712961abb04a260210181117e87ae8f7/a29ff81edfcd47fedcda8a760c61ba7b.png"
                  alt="Steven Eric Javidi"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <h1 className="text-2xl font-bold text-neutral-800">Steven Eric Javidi</h1>
              <p className="text-neutral-600">Houston Guide Creator</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Guides Section */}
      <section className="py-16 lg:py-24 bg-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-12 text-center">
            Featured Guides
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* A Christmas Carol */}
            <Link to="/holiday-detail/1" className="bg-neutral-50 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer">
              <div className="h-48 bg-cover bg-center relative" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Alley%20Theatre%20Houston%20interior%20with%20Victorian%20era%20stage%20production%20of%20A%20Christmas%20Carol%2C%20actors%20in%20period%20costumes%20performing%20dramatic%20scene%2C%20warm%20theatrical%20lighting%2C%20audience%20watching%20classic%20holiday%20performance%2C%20professional%20theatre%20atmosphere&width=400&height=300&seq=christmas-carol-home&orientation=landscape')`
              }}>
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-800/20 to-transparent"></div>
                <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 text-xs font-medium rounded-full">
                  HOLIDAY
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  A Christmas Carol
                </h3>
                <p className="text-neutral-600 mb-4">
                  Classic holiday tale at Alley Theatre
                </p>
                <div className="flex items-center text-sm text-neutral-500">
                  <i className="ri-calendar-line mr-2"></i>
                  <span>Nov 21 - Dec 29</span>
                  <i className="ri-map-pin-line ml-4 mr-2"></i>
                  <span>Alley Theatre</span>
                </div>
              </div>
            </Link>

            {/* The Nutcracker */}
            <Link to="/holiday-detail/4" className="bg-neutral-50 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer">
              <div className="h-48 bg-cover bg-center relative" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20Ballet%20performing%20The%20Nutcracker%20with%20ballerinas%20in%20beautiful%20costumes%20on%20grand%20stage%2C%20Sugar%20Plum%20Fairy%20scene%2C%20elegant%20classical%20ballet%20performance%2C%20festive%20holiday%20atmosphere%2C%20professional%20dance%20production&width=400&height=300&seq=nutcracker-ballet-home&orientation=landscape')`
              }}>
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-800/20 to-transparent"></div>
                <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 text-xs font-medium rounded-full">
                  HOLIDAY
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  The Nutcracker
                </h3>
                <p className="text-neutral-600 mb-4">
                  Houston Ballet's beloved holiday tradition
                </p>
                <div className="flex items-center text-sm text-neutral-500">
                  <i className="ri-calendar-line mr-2"></i>
                  <span>Nov 29 - Dec 29</span>
                  <i className="ri-map-pin-line ml-4 mr-2"></i>
                  <span>Hobby Center</span>
                </div>
              </div>
            </Link>

            {/* Nutcracker Market */}
            <Link to="/holiday-detail/5" className="bg-neutral-50 rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer">
              <div className="h-48 bg-cover bg-center relative" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Nutcracker%20Market%20Houston%20with%20festive%20holiday%20shopping%20booths%2C%20families%20browsing%20unique%20gifts%20and%20decorations%2C%20colorful%20vendor%20displays%2C%20indoor%20market%20atmosphere%2C%20holiday%20shopping%20experience&width=400&height=300&seq=nutcracker-market-home&orientation=landscape')`
              }}>
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-800/20 to-transparent"></div>
                <div className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 text-xs font-medium rounded-full">
                  HOLIDAY
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  Nutcracker Market®
                </h3>
                <p className="text-neutral-600 mb-4">
                  Houston's premier holiday shopping event
                </p>
                <div className="flex items-center text-sm text-neutral-500">
                  <i className="ri-calendar-line mr-2"></i>
                  <span>Nov 6-16</span>
                  <i className="ri-map-pin-line ml-4 mr-2"></i>
                  <span>NRG Park</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* YouTube Videos Section */}
      <section className="py-16 lg:py-24 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-6">
              Watch Houston Stories
            </h2>
            <p className="text-lg text-neutral-600 mb-8">
              I'm Steven Eric Javidi, the creator behind Eric Javidi – Houston Guide, where I share short, engaging videos that highlight the best of Houston — from seasonal festivals and hidden parks to local history, food, and culture.
            </p>
            <a
              href="https://www.youtube.com/@EricJavidi"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-youtube-fill mr-2 text-xl"></i>
              Subscribe to YouTube Channel
            </a>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Video 1: Most Haunted Places */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/N2B3LvmIL3k"
                  title="Most Haunted Places in Houston 👻 | Eric Javidi – Houston Guide"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  Most Haunted Places in Houston 👻
                </h3>
                <p className="text-neutral-600 mb-4">
                  Discover Houston's spookiest locations and ghostly legends in this quick guide to the city's most haunted spots.
                </p>
                <div className="flex items-center text-sm text-neutral-500">
                  <i className="ri-time-line mr-2"></i>
                  <span>1:30</span>
                  <i className="ri-ghost-line ml-4 mr-2"></i>
                  <span>Halloween</span>
                </div>
              </div>
            </div>

            {/* Video 2: Best Houston Parks */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/PxgoC5hgKK8"
                  title="Best Houston Parks to Visit in Fall 🍂 | Eric Javidi – Houston Guide"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  Best Houston Parks to Visit in Fall 🍂
                </h3>
                <p className="text-neutral-600 mb-4">
                  Explore Houston's most beautiful parks during fall season, perfect for family outings and nature walks.
                </p>
                <div className="flex items-center text-sm text-neutral-500">
                  <i className="ri-time-line mr-2"></i>
                  <span>1:45</span>
                  <i className="ri-leaf-line ml-4 mr-2"></i>
                  <span>Fall Season</span>
                </div>
              </div>
            </div>

            {/* Video 3: Best Houston Fall Festivals */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/iuYWZRlFDyg"
                  title="Best Houston Fall Festivals 🎃 | Eric Javidi – Houston Guide"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  Best Houston Fall Festivals 🎃
                </h3>
                <p className="text-neutral-600 mb-4">
                  Your guide to Houston's top fall festivals and seasonal celebrations happening around the city.
                </p>
                <div className="flex items-center text-sm text-neutral-500">
                  <i className="ri-time-line mr-2"></i>
                  <span>1:58</span>
                  <i className="ri-calendar-line ml-4 mr-2"></i>
                  <span>Festivals</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fall Events Section - NEW */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-4">
              Upcoming Holiday Events in Houston
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Experience Houston's magical holiday season with festive celebrations, family-friendly activities, and cultural traditions across the city.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Zoo Lights */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
              <div 
                className="h-48 bg-cover bg-center relative"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20Zoo%20at%20night%20with%20colorful%20Christmas%20lights%20illuminating%20trees%20and%20pathways%2C%20families%20walking%20through%20magical%20light%20displays%2C%20festive%20animal-themed%20light%20sculptures%2C%20holiday%20atmosphere%20with%20twinkling%20lights&width=400&height=300&seq=zoo-lights-home&orientation=landscape')`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-800/20 to-transparent"></div>
                <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 text-xs font-bold rounded-full">
                  NOV 21 - JAN 4
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  Zoo Lights at Houston Zoo
                </h3>
                <p className="text-neutral-600 mb-4 text-sm">
                  Magical evening light displays perfect for families with immersive holiday experiences.
                </p>
                <div className="flex items-center text-sm text-neutral-500 mb-3">
                  <i className="ri-map-pin-line mr-2"></i>
                  <span>Houston Zoo</span>
                </div>
                <div className="flex items-center text-sm text-blue-600 font-medium">
                  <i className="ri-calendar-event-line mr-2"></i>
                  <span>November 21 - January 4</span>
                </div>
              </div>
            </div>

            {/* Old Town Spring */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
              <div 
                className="h-48 bg-cover bg-center relative"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Old%20Town%20Spring%20Texas%20decorated%20for%20Christmas%20with%20historic%20storefronts%2C%20twinkling%20holiday%20lights%2C%20festive%20shopping%20district%2C%20families%20browsing%20charming%20shops%2C%20small%20town%20holiday%20atmosphere%20with%20decorated%20trees&width=400&height=300&seq=old-town-spring-home&orientation=landscape')`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-800/20 to-transparent"></div>
                <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 text-xs font-bold rounded-full">
                  NOV 22 - DEC 21
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  Home for the Holidays in Old Town Spring
                </h3>
                <p className="text-neutral-600 mb-4 text-sm">
                  Charming holiday shopping, lights, photos with Santa, and festive entertainment in historic Spring.
                </p>
                <div className="flex items-center text-sm text-neutral-500 mb-3">
                  <i className="ri-map-pin-line mr-2"></i>
                  <span>Old Town Spring</span>
                </div>
                <div className="flex items-center text-sm text-blue-600 font-medium">
                  <i className="ri-calendar-event-line mr-2"></i>
                  <span>Nov 22 - Dec 21 (Weekends)</span>
                </div>
              </div>
            </div>

            {/* Breakfast with Santa */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow">
              <div 
                className="h-48 bg-cover bg-center relative"
                style={{
                  backgroundImage: `url('https://readdy.ai/api/search-image?query=Downtown%20Aquarium%20Houston%20decorated%20for%20Christmas%20with%20children%20meeting%20Santa%20Claus%2C%20festive%20breakfast%20buffet%20setting%2C%20families%20dining%20with%20holiday%20decorations%2C%20magical%20underwater%20themed%20Christmas%20atmosphere&width=400&height=300&seq=breakfast-santa-home&orientation=landscape')`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-800/20 to-transparent"></div>
                <div className="absolute top-3 right-3 bg-red-500 text-white px-3 py-1 text-xs font-bold rounded-full">
                  DEC 6-24
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                  Breakfast with Santa at Downtown Aquarium
                </h3>
                <p className="text-neutral-600 mb-4 text-sm">
                  Special breakfast buffet with Santa, perfect for creating magical holiday memories with children.
                </p>
                <div className="flex items-center text-sm text-neutral-500 mb-3">
                  <i className="ri-map-pin-line mr-2"></i>
                  <span>Downtown Aquarium</span>
                </div>
                <div className="flex items-center text-sm text-blue-600 font-medium">
                  <i className="ri-calendar-event-line mr-2"></i>
                  <span>Dec 6, 13, 14, 20-24</span>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <Link
              to="/holiday-season"
              className="inline-flex items-center px-8 py-4 bg-red-600 text-white text-lg font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              View All Holiday Events
              <i className="ri-arrow-right-line ml-2 text-xl"></i>
            </Link>
            <p className="text-sm text-neutral-600 mt-4">
              Discover 10+ festive celebrations happening across Houston this holiday season
            </p>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-16 lg:py-24 bg-neutral-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl lg:text-5xl font-bold mb-12">
              Approach
            </h2>
            <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-search-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">
                  Research-Driven
                </h3>
                <p className="text-xl text-neutral-200 leading-relaxed">
                  Every guide is built on meticulous local research, historical records, and verified information to ensure accuracy and depth
                </p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-building-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">
                  Cultural & Natural
                </h3>
                <p className="text-xl text-neutral-200 leading-relaxed">
                  Seamlessly blends Houston's rich cultural heritage with its stunning natural landscapes
                </p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className="ri-group-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">
                  For Everyone
                </h3>
                <p className="text-xl text-neutral-200 leading-relaxed">
                  Thoughtfully designed experiences that welcome both longtime Houstonians and first-time visitors
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Eric Javidi Bio Section (modified) */}
      <section className="py-16 lg:py-24 bg-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="lg:order-2">
              <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-6">
                About Eric Javidi
              </h2>
              <p className="text-lg text-neutral-600 mb-6">
                Eric is a Houston-based storyteller who explores where the city's rich history intersects with its natural landscapes. With a passion for uncovering the stories that make Houston unique, Eric combines meticulous research with field-tested exploration to create guides that serve both longtime residents and curious visitors.
              </p>
              <p className="text-lg text-neutral-600 mb-8">
                His work focuses on making Houston's diverse neighborhoods, cultural landmarks, and green spaces accessible through carefully crafted routes that blend historical context with practical information for families and individuals alike.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover-bg-green-700 whitespace-nowrap cursor-pointer"
              >
                Learn More About Eric
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
            <div className="lg:order-1">
              <div className="w-full h-96 bg-cover bg-center rounded-lg shadow-lg">
                <img
                  src="https://static.readdy.ai/image/712961abb04a260210181117e87ae8f7/9daeab8ab2163d3cb6031416cc855f0d.png"
                  alt="Eric Javidi"
                  className="w-full h-full object-cover object-center rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Houston Special Section */}
      <section className="py-16 lg:py-24 bg-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-8">
              Why Eric Explores Houston
            </h2>
            <p className="text-lg text-neutral-600 mb-12">
              Houston's story isn't told in guidebooks—it's written in the architecture of the Heights, the trails of Buffalo Bayou, and the flavors of East End taquerías. Eric believes every neighborhood has stories worth sharing.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-home-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Diverse Neighborhoods
                </h3>
                <p className="text-sm text-neutral-600">
                  From historic Heights to vibrant East End
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-leaf-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Urban Nature
                </h3>
                <p className="text-sm text-neutral-600">
                  Bayous, parks, and green spaces throughout the city
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-restaurant-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Culinary Heritage
                </h3>
                <p className="text-sm text-neutral-600">
                  Authentic flavors reflecting Houston's diversity
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                  Rich History
                </h3>
                <p className="text-sm text-neutral-600">
                  Stories spanning from early settlements to modern growth
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 lg:py-24 bg-green-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-6">
            Start Your Houston Discovery
          </h2>
          <p className="text-lg text-neutral-600 mb-8">
            Whether you're a lifelong Houstonian or just visiting, Eric's guides offer fresh perspectives on the city's hidden gems and well-known treasures.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/guides"
              className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-green-600 hover-bg-green-7 whitespace-nowrap cursor-pointer"
            >
              Browse All Guides
              <i className="ri-map-line ml-2"></i>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-3 border-2 border-green-600 text-base font-medium rounded-md text-green-600 bg-transparent hover-bg-green-50 whitespace-nowrap cursor-pointer"
            >
              Get In Touch
              <i className="ri-mail-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-xl font-semibold mb-4">Eric Javidi</h3>
              <p className="text-neutral-300 mb-4">
                Houston-based storyteller exploring where the city's history meets its natural landscapes.
              </p>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.youtube.com/@EricJavidi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-red-400 transition-colors"
                  aria-label="YouTube Channel"
                >
                  <i className="ri-youtube-fill text-2xl"></i>
                </a>
                <a
                  href="https://www.facebook.com/ericjavidihouston/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-300 hover:text-blue-400 transition-colors"
                  aria-label="Facebook Page"
                >
                  <i className="ri-facebook-fill text-2xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-neutral-300 hover:text-white">About</Link></li>
                <li><Link to="/guides" className="text-neutral-300 hover:text-white">Guides</Link></li>
                <li><Link to="/holiday-season" className="text-neutral-300 hover:text-white">Holiday Season</Link></li>
                <li><Link to="/fall-festivals" className="text-neutral-300 hover:text-white">Fall Festivals</Link></li>
                <li><Link to="/resources" className="text-neutral-300 hover=text-white">Resources</Link></li>
                <li><Link to="/contact" className="text-neutral-300 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.youtube.com/@EricJavidi" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-white">YouTube Channel</a></li>
                <li><a href="https://www.facebook.com/ericjavidihouston/" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-white">Facebook Page</a></li>
                <li><a href="https://readdy.ai/?origin=logo" className="text-neutral-300 hover:text-white">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-neutral-700 mt-8 pt-8 text-center">
            <p className="text-neutral-400">
              © 2024 Steven Eric Javidi. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

